/**
 * A line diff for "Show changes" (RFC 0277 3f): what is on disk now against
 * the draft the person typed. Longest common subsequence over lines, which is
 * plenty for one file someone is looking at; past a size where that gets slow
 * it gives up and says so, rather than freezing the window.
 */

export type DiffLine = { kind: 'same' | 'mine' | 'theirs'; text: string }

/** Lines compared at most, as a product, before giving up. */
const MAX_CELLS = 4_000_000

/** Null when the files are too large to diff here. */
export function lineDiff(mine: string, theirs: string): DiffLine[] | null {
  const a = mine.split('\n')
  const b = theirs.split('\n')
  // Common head and tail cost nothing and are most of a real file.
  let head = 0
  while (head < a.length && head < b.length && a[head] === b[head]) head++
  let tail = 0
  while (
    tail < a.length - head &&
    tail < b.length - head &&
    a[a.length - 1 - tail] === b[b.length - 1 - tail]
  )
    tail++
  const am = a.slice(head, a.length - tail)
  const bm = b.slice(head, b.length - tail)
  if (am.length * bm.length > MAX_CELLS) return null

  // lcs[i][j]: common length of am[i..] and bm[j..].
  const w = bm.length + 1
  const lcs = new Uint32Array((am.length + 1) * w)
  for (let i = am.length - 1; i >= 0; i--)
    for (let j = bm.length - 1; j >= 0; j--)
      lcs[i * w + j] =
        am[i] === bm[j]
          ? lcs[(i + 1) * w + j + 1] + 1
          : Math.max(lcs[(i + 1) * w + j], lcs[i * w + j + 1])

  const out: DiffLine[] = a
    .slice(0, head)
    .map((text) => ({ kind: 'same' as const, text }))
  let i = 0
  let j = 0
  while (i < am.length || j < bm.length) {
    if (i < am.length && j < bm.length && am[i] === bm[j]) {
      out.push({ kind: 'same', text: am[i] })
      i++
      j++
    } else if (
      i < am.length &&
      (j >= bm.length || lcs[(i + 1) * w + j] >= lcs[i * w + j + 1])
    ) {
      // Yours before theirs on a tie, the order a unified diff reads in.
      out.push({ kind: 'mine', text: am[i] })
      i++
    } else {
      out.push({ kind: 'theirs', text: bm[j] })
      j++
    }
  }
  for (const text of a.slice(a.length - tail)) out.push({ kind: 'same', text })
  return out
}
